import { VidarioClientProvider } from "@vidario/api-client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState, type PropsWithChildren, type ReactElement } from "react";

import { resolveAuthClient } from "../platform/auth";
import { resolveClient } from "../platform/api";
import { AuthProvider } from "./auth-provider";

/**
 * Top-level app providers. QueryClient wraps both the typed API client
 * and the auth provider so sign-in / sign-out can invalidate user-scoped
 * queries (spec §3: TanStack Query is the single source of async truth).
 */
export function AppProviders({ children }: PropsWithChildren): ReactElement {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const apiClient = useMemo(() => resolveClient(), []);
  const authClient = useMemo(() => resolveAuthClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <VidarioClientProvider client={apiClient}>
        <AuthProvider client={authClient}>{children}</AuthProvider>
      </VidarioClientProvider>
    </QueryClientProvider>
  );
}
